import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ShadeDialog";
import { getOrLoadStickerDesign } from './StickerDesigns';
import { StickerDesign, Board } from '../utils/Primitives';
import { getPaymentMethods, getPaymentMethod } from '../payments/PaymentMethods';
import { useAccount, useChainId, useWriteContract } from 'wagmi';
import { Address, formatEther, parseEther } from 'viem';
import { useStickerContract } from './StickerDesignsAbs';
import { getPrimaryExplorerUrl } from '../utils/GeneralUtilities';
import { abi } from './StickerDesignsAbi';
import { pridi } from '../fonts';

const StickerContractAddress = process.env.NEXT_PUBLIC_STICKER_DESIGNS_CONTRACT_ADDRESS as `0x${string}` | null;

type PublishingReviewerProps = {
  stickerId: number | null;
  boardRef: React.MutableRefObject<Board>;
  onClose: () => void;
};

export default function PublishingReviewer({ stickerId, boardRef, onClose }: PublishingReviewerProps) {
  const { address: playerAddress } = useAccount();
  const chainId = useChainId();
  const [sticker, setSticker] = useState<StickerDesign | null>(null);
  const [newPrice, setNewPrice] = useState<string>('0.002');
  const [newPaymentMethodId, setNewPaymentMethodId] = useState<number>(0);
  const [newLimitToHolders, setNewLimitToHolders] = useState<string>('');
  const [isCapped, setIsCapped] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [transactionHash, setTransactionHash] = useState<string | null>(null);
  const { writeContract } = useWriteContract();
  const { capSticker, setStickerLimitToHolders, setStickerPrice } = useStickerContract();

  useEffect(() => {
    if (stickerId !== null) {
      getOrLoadStickerDesign({ boardRef, stickerId }).then(loadedSticker => {
        if (loadedSticker) {
          setSticker(loadedSticker);
          setNewPrice(formatEther(BigInt(loadedSticker.price)));
          setNewPaymentMethodId(loadedSticker.paymentMethodId);
          setNewLimitToHolders(loadedSticker.limitToHolders || '');
          setIsCapped(loadedSticker.endTime !== null);
        }
      });
    }
  }, [stickerId, boardRef]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPrice(e.target.value);
  };

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewPaymentMethodId(parseInt(e.target.value));
  };

  const handleLimitToHoldersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewLimitToHolders(e.target.value);
  };

  const handleSetPrice = async () => {
    if (!sticker || !playerAddress || !StickerContractAddress) return;
    setIsLoading(true);
    try {
      const priceInWei = parseEther(newPrice);
      const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId: newPaymentMethodId });
      if (!paymentMethod) {
        throw new Error('Payment method not found');
      }
      // Convert wei to the appropriate number based on token decimals
      const priceInSmallestUnit = Number(priceInWei) / Math.pow(10, paymentMethod.decimals);
      const priceInWeiBigInt: bigint = BigInt(Math.floor(priceInSmallestUnit * Math.pow(10, paymentMethod.decimals)));

      writeContract({
        abi,
        address: StickerContractAddress,
        functionName: 'setStickerPrice',
        args: [BigInt(sticker.id), BigInt(newPaymentMethodId), priceInWeiBigInt],
      }, {
        onSuccess: (data) => {
          setTransactionHash(data);
          setIsLoading(false);
        },
        onError: (error) => {
          console.error('Error setting price:', error);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.error('Error setting price:', error);
      setIsLoading(false);
    }
  };

  const handleSetLimitToHolders = async () => {
    if (!sticker || !playerAddress || !StickerContractAddress) return;
    setIsLoading(true);
    try {
      writeContract({
        abi,
        address: StickerContractAddress,
        functionName: 'setStickerLimitToHolders',
        args: [BigInt(sticker.id), newLimitToHolders as Address],
      }, {
        onSuccess: (data) => {
          setTransactionHash(data);
          setIsLoading(false);
        },
        onError: (error) => {
          console.error('Error setting limit to holders:', error);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.error('Error setting limit to holders:', error);
      setIsLoading(false);
    }
  };

  const handleCapSticker = async () => {
    if (!sticker || !playerAddress || !StickerContractAddress) return;
    setIsLoading(true);
    try {
      writeContract({
        abi,
        address: StickerContractAddress,
        functionName: 'capSticker',
        args: [BigInt(sticker.id)],
      }, {
        onSuccess: (data) => {
          setTransactionHash(data);
          setIsCapped(true);
          setIsLoading(false);
        },
        onError: (error) => {
          console.error('Error capping sticker:', error);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.error('Error capping sticker:', error);
      setIsLoading(false);
    }
  };

  if (stickerId === null || !sticker) {
    return null;
  }

  const paymentMethods = getPaymentMethods({ boardRef });
  const isPublisher = playerAddress?.toLowerCase() === sticker.currentPublisher.toLowerCase();

  if (!isPublisher) {
    return (
      <Dialog open={stickerId !== null} onOpenChange={onClose}>
        <DialogContent className='bg-white text-slate-900 p-4'>
          <DialogHeader>
            <DialogTitle className={`text-2xl ${pridi.className}`}>Publisher Controls</DialogTitle>
            <div className="text-center mt-4">
              Only the sticker publisher can modify these settings.
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={stickerId !== null} onOpenChange={onClose}>
      <DialogContent className='bg-white text-slate-900 px-4 py-8'>
        <DialogHeader>
          <DialogTitle className={`text-2xl ${pridi.className}`}>Publisher Controls: Sticker #{sticker.id}</DialogTitle>
          <div className="space-y-6 py-8 px-2">
            {/* Price Settings */}
            <div className="space-y-2 py-2">
              <h3 className="font-semibold">Price Settings</h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newPrice}
                  onChange={handlePriceChange}
                  className="border rounded px-2 py-1 w-24"
                  placeholder="0.002"
                />
                <select
                  value={newPaymentMethodId}
                  onChange={handlePaymentMethodChange}
                  className="border rounded px-2 py-1"
                >
                  {Object.values(paymentMethods).map((method) => (
                    method && (
                      <option key={method.id} value={method.id}>
                        {method.symbol}
                      </option>
                    )
                  ))}
                </select>
                <button
                  onClick={handleSetPrice}
                  disabled={isLoading}
                  className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Update Price
                </button>
              </div>
            </div>

            {/* Limit to Holders */}
            <div className="space-y-2 py-2">
              <h3 className="font-semibold">Limit to Holders</h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newLimitToHolders}
                  onChange={handleLimitToHoldersChange}
                  placeholder="Contract address"
                  className="border rounded px-2 py-1 flex-1"
                />
                <button
                  onClick={handleSetLimitToHolders}
                  disabled={isLoading}
                  className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Update
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Set an ERC20 or ERC721 contract address. Only holders of this token can slap the sticker.
              </p>
            </div>

            {/* Cap Sticker */}
            <div className="space-y-2 py-2">
              <h3 className="font-semibold">Cap Sticker</h3>
              <button
                onClick={handleCapSticker}
                disabled={isLoading || isCapped}
                className="px-4 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                {isCapped ? 'Sticker is Capped' : 'Cap Sticker'}
              </button>
              <p className="text-sm text-gray-600">
                Capping a sticker prevents any new slaps from being created.
              </p>
            </div>

            {/* Transaction Link */}
            {transactionHash && (
              <div className="mt-4 p-2 bg-green-100 text-green-700 rounded-md">
                Price change transaction created:{' '}
                <a
                  href={`${getPrimaryExplorerUrl(chainId)}/tx/${transactionHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-green-800"
                >
                  View
                </a>
              </div>
            )}
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}